import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { font, themedColor } from '@lemonade-hq/cdk';
import { ReviewProduct } from './types';
import chevron from '@/static/assets/chevron.png';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { motion } from 'motion/react';

const FLOAT_BOTTOM = 48;

const FilterWrapper = styled(motion.div)<{
  shrink: boolean;
  position: string;
  bottom: string;
}>`
  position: ${({ position }) => position};
  bottom: ${({ bottom }) => bottom};
  padding: 12px;
  display: flex;
  z-index: 2;
  &::before {
    box-shadow:
      0px 12px 32px -8px rgba(0, 0, 0, 0.08),
      -2px -2px 10px -1px rgba(0, 0, 0, 0.08);
    height: 100%;
    border: 1px solid ${themedColor('borderPrimary')};
    background-color: ${themedColor('white')};
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 100px;
    width: ${({ shrink }) => (shrink ? '52.8px' : '100%')};
    transition: width 0.2s ease-in-out;
    transition-delay: ${({ shrink }) => (shrink ? '0.1' : '0')}s;
  }
`;

const FilterPlaceholder = styled.div<{ show: boolean }>`
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

const ProductFilter = styled.div<{ selected: boolean; show: boolean }>`
  ${({ selected }) =>
    font('main', {
      fontSize: '1.6rem',
      fontWeight: selected ? 'bold' : 'normal',
    })};
  z-index: 1;
  padding: 4px 12px;
  border-radius: 100px;
  background-color: ${({ selected }) => (selected ? themedColor('whiteSmoke') : themedColor('white'))};
  color: ${({ selected }) => (selected ? themedColor('night') : themedColor('mouseGray'))};
  cursor: pointer;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition-delay: ${({ show }) => (show ? '0.2' : '0')}s;
  pointer-events: ${({ show }) => (show ? 'auto' : 'none')};
  transition:
    opacity 0.2s ease-in-out,
    color 0.1s ease-in-out,
    background-color 0.2s ease-in-out;
  &:hover {
    color: ${themedColor('night')};
  }
  ${MEDIA_QUERIES.mobile`
     padding: 4px 10px;
  `}
`;

const IconWrapper = styled.div<{ show: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 60px;
  border-radius: 100px;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  transition-delay: ${({ show }) => (show ? '0.2' : '0')}s;
  pointer-events: ${({ show }) => (show ? 'auto' : 'none')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChevronIcon = styled.img`
  rotate: 180deg;
  width: 25px;
  margin-bottom: 5px;
`;

interface FilterProps {
  selectedFilter: ReviewProduct;
  setSelectedFilter: (product: ReviewProduct) => void;
}

export const Filter: React.FC<FilterProps> = ({ setSelectedFilter, selectedFilter }) => {
  const filterRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);

  const [isFloating, setIsFloating] = useState(false);
  const [position, setPosition] = useState('relative');
  const [bottom, setBottom] = useState('0px');
  const height = useMemo(() => filterRef.current?.offsetHeight ?? 0, [filterRef.current]);

  const updatePlaceholderHeight = useCallback(() => {
    placeholderRef.current?.style.setProperty('height', `${height}px`);
  }, [height]);

  const handleScroll = useCallback(() => {
    if (!filterRef.current || !placeholderRef.current) return;

    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const finalPosition = scrollPosition + windowHeight;

    const filterOffset = filterRef.current.offsetTop;
    const placeholderOffset = placeholderRef.current.offsetTop;

    const floatingThreshold = filterOffset + height + FLOAT_BOTTOM;
    const relativeThreshold = placeholderOffset + height + FLOAT_BOTTOM;

    if (finalPosition < floatingThreshold && !isFloating) {
      setPosition('fixed');
      setBottom(`${FLOAT_BOTTOM}px`);
      setIsFloating(true);
    } else if (finalPosition >= relativeThreshold && isFloating) {
      setPosition('relative');
      setBottom('0px');
      setIsFloating(false);
    }
  }, [height, isFloating]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    updatePlaceholderHeight();
  }, [isFloating, updatePlaceholderHeight]);

  const onProductClick = useCallback(
    (product: ReviewProduct): void => {
      setSelectedFilter(product);
      window.history.pushState(
        null,
        '',
        product !== ReviewProduct.All ? `/reviews/${product.toLowerCase()}` : '/reviews',
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSelectedFilter],
  );

  return (
    <>
      <FilterWrapper
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        ref={filterRef}
        shrink={!isFloating}
        position={position}
        bottom={bottom}
      >
        {Object.values(ReviewProduct)
          .filter(product => product !== ReviewProduct.Life)
          .map(product => (
            <ProductFilter
              key={product}
              selected={selectedFilter === product}
              onClick={() => onProductClick(product)}
              data-product={product}
              role="button"
              show={isFloating}
            >
              {product}
            </ProductFilter>
          ))}
        <IconWrapper show={!isFloating} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <ChevronIcon src={chevron.src} />
        </IconWrapper>
      </FilterWrapper>
      <FilterPlaceholder show={isFloating} ref={placeholderRef} />
    </>
  );
};
